import React from 'react';

const RiscosSemAssessoria: React.FC = () => {
  return (
    <section className="relative bg-gradient-to-br from-slate-50 via-zinc-50 to-gray-100 py-12 sm:py-16 lg:py-28 overflow-hidden">
      {/* Background pattern overlay - very subtle */}
      <div className="absolute inset-0 opacity-[0.025]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        ></div>
      </div>

      {/* Soft accent glows - very light */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-50/80 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-zinc-200/60 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[500px] lg:min-h-[620px]">
          
          {/* Coluna Esquerda - Conteúdo */}
          <div className="space-y-6 sm:space-y-8">
            
            {/* Badge Superior */}
            <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 sm:px-5 py-2">
              <i className="ri-alert-line text-red-600 text-base sm:text-lg"></i>
              <span className="text-red-700 font-bold text-xs sm:text-sm tracking-[0.15em] uppercase">Riscos Reais</span>
            </div>

            {/* Título */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 leading-[1.15]">
              O maior risco não é o imprevisto. É{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-500">
                estar desprotegido
              </span>{' '}
              quando ele acontece.
            </h2>

            {/* Texto de Apoio */}
            <p className="text-slate-600 text-base sm:text-lg lg:text-xl leading-relaxed">
              Erros na contratação de seguros acontecem todos os dias e deixam o segurado completamente desamparado.
            </p>

            {/* Blocos de Risco */}
            <div className="space-y-3 sm:space-y-4">
              {[
                {
                  icon: 'ri-car-line',
                  title: 'Seguro auto que não cobre o uso real do veículo'
                },
                {
                  icon: 'ri-home-4-line',
                  title: 'Seguro residencial abaixo do valor do patrimônio'
                },
                {
                  icon: 'ri-shield-cross-line',
                  title: 'Seguro de vida mal estruturado'
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: 'Plano de saúde com limitações críticas'
                }
              ].map((risco, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 sm:gap-4 bg-white border border-slate-200 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:border-red-300 hover:bg-red-50/30 transition-all duration-300 group"
                >
                  <div className="flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 bg-red-50 rounded-lg flex items-center justify-center border border-red-200 group-hover:border-red-300 transition-colors">
                    <i className={`${risco.icon} text-red-500 text-lg sm:text-xl`}></i>
                  </div>
                  <p className="text-slate-700 font-medium text-sm sm:text-base lg:text-lg leading-relaxed pt-1">
                    {risco.title}
                  </p>
                </div>
              ))}
            </div>

            {/* Bloco de Autoridade IBX Brasil */}
            <div className="relative bg-white border border-slate-200 border-l-4 border-l-emerald-500 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-7">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 bg-emerald-50 rounded-lg sm:rounded-xl flex items-center justify-center border border-emerald-200">
                  <i className="ri-shield-check-line text-emerald-600 text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <div className="text-emerald-700 font-bold text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">
                    Solução IBX Brasil
                  </div>
                  <p className="text-slate-800 text-base sm:text-lg lg:text-xl leading-relaxed font-semibold">
                    Com a assessoria da IBX Brasil, cada cobertura é planejada de forma estratégica para eliminar essas falhas.
                  </p>
                </div>
              </div>
            </div>

            {/* Indicadores Visuais */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-1">
              {[
                { label: 'Auto', desc: 'Cobertura compatível com o uso real' },
                { label: 'Patrimônio', desc: 'Proteção proporcional ao valor exposto' },
                { label: 'Família', desc: 'Segurança contratual mais eficiente' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-lg p-4 hover:border-emerald-300 hover:bg-emerald-50/20 transition-all duration-300"
                >
                  <div className="text-emerald-700 font-bold text-xs sm:text-sm uppercase tracking-wider mb-1">
                    {item.label}
                  </div>
                  <div className="text-slate-500 text-xs leading-snug">
                    {item.desc}
                  </div>
                </div>
              ))}
            </div>

            {/* Destaque Final */}
            <div className="relative bg-slate-900 rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg">
              <div className="absolute -left-0 top-1/2 -translate-y-1/2 w-1 h-14 sm:h-16 bg-gradient-to-b from-emerald-500 to-green-600 rounded-r"></div>
              <div className="flex items-start gap-2.5 sm:gap-3 pl-3">
                <i className="ri-double-quotes-l text-emerald-400/30 text-2xl sm:text-3xl flex-shrink-0"></i>
                <p className="text-white text-base sm:text-lg lg:text-xl font-bold leading-relaxed">
                  O mercado vende seguros. A IBX Brasil estrutura proteção.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-2 sm:pt-4">
              <a
                href="#analise-gratuita"
                className="group flex sm:inline-flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-action-orange-500 to-red-500 hover:from-action-orange-600 hover:to-red-600 text-white font-bold px-6 sm:px-8 py-4 sm:py-5 rounded-xl transition-all duration-300 w-full sm:w-auto text-center"
              >
                <i className="ri-shield-check-line text-xl sm:text-2xl shrink-0"></i>
                <span className="text-sm sm:text-base lg:text-lg leading-tight">Quero proteger meu patrimônio com mais segurança</span>
                <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform shrink-0"></i>
              </a>
              <p className="text-slate-500 text-xs sm:text-sm mt-3 sm:mt-4 italic flex items-center justify-center sm:justify-start gap-2">
                <i className="ri-shield-star-line text-emerald-500"></i>
                Sem compromisso. Proteção estruturada em até 48h.
              </p>
            </div>

          </div>

          {/* Coluna Direita - Imagem */}
          <div className="relative h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[620px] mt-4 lg:mt-0">
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">
              {/* Imagem Principal */}
              <img
                src="https://readdy.ai/api/search-image?query=worried%20business%20professional%20analyzing%20insurance%20documents%20contract%20review%20sitting%20at%20modern%20corporate%20office%20desk%20with%20concerned%20expression%20professional%20lighting%20corporate%20environment%20premium%20photography%20style%20realistic%20high%20quality%20focus%20on%20person%20reading%20contract%20papers%20with%20visible%20concern%20about%20coverage%20gaps&width=800&height=900&seq=riscos-sem-assessoria-main&orientation=portrait"
                alt="Profissional preocupado analisando contrato de seguro"
                className="w-full h-full object-cover"
              />
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent"></div>

              {/* Badge superior direito - Estatística */}
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-red-600 text-white rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 border border-red-500/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                  <i className="ri-shield-cross-fill text-base sm:text-lg"></i>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider opacity-90">Risco</span>
                </div>
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold leading-none">68%</div>
                <div className="text-[10px] sm:text-xs font-medium opacity-90 whitespace-nowrap">seguros mal estruturados</div>
              </div>

              {/* Card Flutuante Inferior */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 border border-slate-200">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-600 to-orange-600 rounded-lg flex items-center justify-center">
                    <i className="ri-error-warning-line text-white text-xl sm:text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold text-base sm:text-lg mb-0.5 sm:mb-1">Proteção insuficiente</h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      As falhas só aparecem quando o prejuízo já aconteceu
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default RiscosSemAssessoria;